import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getProducts, getProductFilters } from '../lib/api';
import { formatCurrency, getStatusColor, getStatusLabel, truncate, debounce } from '../lib/utils';
import {
    MagnifyingGlass, Package, X, Plus, Image as ImageIcon,
    CaretLeft, CaretRight, Export, FileCsv, FileXls, Check
} from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

function ProductThumbnail({ image, sku }) {
    const [imgError, setImgError] = useState(false);
    
    const src = image?.url || image?.file_url || (image?.storage_path && image?.id 
        ? `${API_URL}/api/files/${image.id}/download?auth=${localStorage.getItem('token')}`
        : null);
    
    if (!src || imgError) {
        return (
            <div className="w-10 h-10 rounded bg-muted flex items-center justify-center flex-shrink-0" data-testid="product-thumbnail-placeholder">
                <ImageIcon size={16} className="text-muted-foreground" />
            </div>
        );
    }
    
    return (
        <img 
            src={src} 
            alt={sku} 
            className="w-10 h-10 rounded object-cover flex-shrink-0 border border-border"
            onError={() => setImgError(true)}
            data-testid="product-thumbnail"
        />
    );
}

export default function ProductList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { isAdmin, can } = useAuth();
    
    const [products, setProducts] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [categories, setCategories] = useState([]);
    const [subcategoryMap, setSubcategoryMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [filters, setFilters] = useState({
        search: searchParams.get('search') || '',
        category: searchParams.get('category') || '',
        subcategory: searchParams.get('subcategory') || '',
        status: searchParams.get('status') || '',
    });
    const [exportOpen, setExportOpen] = useState(false);
    const [exportExtras, setExportExtras] = useState({ specs: false, pricing: false, packaging: false, media: false });
    const [exporting, setExporting] = useState(false);

    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    const fetchProducts = useMemo(() => 
        debounce(async (filterParams, pageNum) => {
            setLoading(true);
            try {
                const params = { limit: PAGE_SIZE, skip: (pageNum - 1) * PAGE_SIZE };
                if (filterParams.search) params.search = filterParams.search;
                if (filterParams.category) params.category = filterParams.category;
                if (filterParams.subcategory) params.subcategory = filterParams.subcategory;
                if (filterParams.status) params.status = filterParams.status;
                
                const response = await getProducts(params);
                const data = response.data;
                setProducts(data.products || data);
                setTotalCount(data.total_count || (data.products || data).length);
            } catch (error) {
                console.error('Failed to fetch products:', error);
            } finally {
                setLoading(false);
            }
        }, 300),
    []);

    useEffect(() => {
        fetchProducts(filters, page);
    }, [filters, page, fetchProducts]);

    useEffect(() => {
        const loadFilters = async () => {
            try {
                const res = await getProductFilters();
                setCategories(res.data.categories || []);
                setSubcategoryMap(res.data.subcategories || {});
            } catch (error) {
                console.error('Failed to load filter options:', error);
            }
        };
        loadFilters();
    }, []);

    const updateFilter = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        // Clear subcategory when category changes
        if (key === 'category') {
            newFilters.subcategory = '';
        }
        setFilters(newFilters);
        setPage(1);
        
        const newParams = new URLSearchParams();
        Object.entries(newFilters).forEach(([k, v]) => { if (v) newParams.set(k, v); });
        newParams.set('page', '1');
        setSearchParams(newParams);
    };

    const goToPage = (p) => {
        const clamped = Math.max(1, Math.min(p, totalPages));
        setPage(clamped);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', String(clamped));
        setSearchParams(newParams);
    };

    const clearFilters = () => {
        setFilters({ search: '', category: '', subcategory: '', status: '' });
        setPage(1);
        setSearchParams({});
    };

    const activeFilterCount = Object.values(filters).filter(Boolean).length;
    const currentSubcategories = subcategoryMap[filters.category] || [];

    const handleExport = async (fmt) => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            params.set('format', fmt);
            if (filters.search) params.set('search', filters.search);
            if (filters.category) params.set('category', filters.category);
            if (filters.subcategory) params.set('subcategory', filters.subcategory);
            if (filters.status) params.set('status', filters.status);
            const extras = Object.entries(exportExtras).filter(([, v]) => v).map(([k]) => k);
            if (extras.length > 0) params.set('extra_columns', extras.join(','));

            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/products/export?${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Export failed');

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `products_export.${fmt}`;
            a.click();
            window.URL.revokeObjectURL(url);
            setExportOpen(false);
        } catch {
            alert('Export failed');
        } finally {
            setExporting(false);
        }
    };

    const toggleExtra = (key) => setExportExtras(prev => ({ ...prev, [key]: !prev[key] }));

    const getProductDisplay = (product) => {
        const specs = product.specifications || {};
        return {
            sku: product.display_sku || product.product_key,
            name: product.product_name,
            category: product.category,
            subcategory: product.subcategory,
            decor_no: product.decor_no,
            decor_name: product.decor_name,
            dimensions: specs.size_mm 
                ? (specs.size_mm.toString().toLowerCase().includes('mm') ? specs.size_mm : `${specs.size_mm} mm`)
                : (specs.length_mm && specs.width_mm 
                    ? `${specs.length_mm} x ${specs.width_mm} mm` 
                    : null),
            thickness: specs.thickness_text || (specs.thickness_mm ? `${specs.thickness_mm} mm` : null),
            eur_price: product.eur_price,
            price_variants: product.price_variants,
            moq: product.computed_moq,
            status: product.status,
            image: product.primary_image,
        };
    };

    const startItem = (page - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(page * PAGE_SIZE, totalCount);

    return (
        <div className="space-y-5" data-testid="products-page">
            {/* Header */}
            <div className="page-header">
                <div>
                    <h1 className="page-title">Product Catalog</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        {loading ? 'Loading...' : `${totalCount} products`}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {can('export_products') && (
                        <Button variant="outline" onClick={() => setExportOpen(true)} data-testid="export-btn">
                            <Export size={18} className="mr-2" />
                            Export
                        </Button>
                    )}
                    {can('edit_products') && (
                        <Button asChild data-testid="add-product-btn">
                            <Link to="/products/new">
                                <Plus size={18} className="mr-2" />
                                Add Product
                            </Link>
                        </Button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="flex-1 min-w-[250px]">
                        <div className="relative">
                            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search SKU, name, decor, category..."
                                className="pl-10"
                                value={filters.search}
                                onChange={(e) => updateFilter('search', e.target.value)}
                                data-testid="product-search-input"
                            />
                        </div>
                    </div>

                    <Select value={filters.category || "all"} onValueChange={(v) => updateFilter('category', v === "all" ? '' : v)}>
                        <SelectTrigger className="w-[180px]" data-testid="category-filter">
                            <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Categories</SelectItem>
                            {categories.map((cat) => (
                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {currentSubcategories.length > 0 && (
                        <Select value={filters.subcategory || "all"} onValueChange={(v) => updateFilter('subcategory', v === "all" ? '' : v)}>
                            <SelectTrigger className="w-[180px]" data-testid="subcategory-filter">
                                <SelectValue placeholder="All Subcategories" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Subcategories</SelectItem>
                                {currentSubcategories.map((sub) => (
                                    <SelectItem key={sub.name} value={sub.name}>
                                        {sub.name} ({sub.count})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}

                    <Select value={filters.status || "all"} onValueChange={(v) => updateFilter('status', v === "all" ? '' : v)}>
                        <SelectTrigger className="w-[140px]" data-testid="status-filter">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                            <SelectItem value="under_development">In Dev</SelectItem>
                        </SelectContent>
                    </Select>

                    {activeFilterCount > 0 && (
                        <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                            <X size={16} className="mr-1" /> Clear ({activeFilterCount})
                        </Button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-12"></TableHead>
                                <TableHead className="w-[110px]">SKU</TableHead>
                                <TableHead>Product Name</TableHead>
                                <TableHead>Category</TableHead>
                                <TableHead>Decor</TableHead>
                                <TableHead>Dimensions</TableHead>
                                <TableHead>Thickness</TableHead>
                                <TableHead className="text-right">EUR Price</TableHead>
                                <TableHead className="text-right">MOQ</TableHead>
                                <TableHead>Status</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                Array(8).fill(0).map((_, i) => (
                                    <TableRow key={i}>
                                        {Array(10).fill(0).map((_, j) => (
                                            <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : products.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} className="h-40 text-center">
                                        <Package size={40} className="mx-auto mb-2 text-muted-foreground opacity-50" />
                                        <p className="text-muted-foreground">No products found</p>
                                        {activeFilterCount > 0 && (
                                            <Button variant="link" size="sm" onClick={clearFilters} className="mt-2">Clear filters</Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                products.map((product) => {
                                    const d = getProductDisplay(product);
                                    const hasPriceVariants = d.price_variants && d.price_variants.length > 1;
                                    return (
                                        <TableRow key={product.product_key} className="cursor-pointer group" data-testid={`product-row-${product.product_key}`}>
                                            <TableCell>
                                                <Link to={`/products/${product.product_key}`}>
                                                    <ProductThumbnail image={d.image} sku={d.sku} />
                                                </Link>
                                            </TableCell>
                                            <TableCell className="font-mono text-sm font-medium">
                                                <Link to={`/products/${product.product_key}`} className="hover:underline group-hover:text-primary transition-colors">
                                                    {d.sku}
                                                </Link>
                                            </TableCell>
                                            <TableCell>
                                                <Link to={`/products/${product.product_key}`} className="hover:underline">
                                                    {truncate(d.name, 35)}
                                                </Link>
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                <div className="text-muted-foreground">{product.category || '\u2014'}</div>
                                                {d.subcategory && d.subcategory !== product.category && (
                                                    <div className="text-[10px] text-muted-foreground/70">{d.subcategory}</div>
                                                )}
                                            </TableCell>
                                            <TableCell className="font-mono text-sm">
                                                <div>{d.decor_no || '\u2014'}</div>
                                                {d.decor_name && <div className="text-xs text-muted-foreground">{d.decor_name}</div>}
                                            </TableCell>
                                            <TableCell className="font-mono text-sm">
                                                {d.dimensions || '\u2014'}
                                            </TableCell>
                                            <TableCell className="font-mono text-sm">
                                                {hasPriceVariants
                                                    ? d.price_variants.map(v => v.thickness_text).join(', ')
                                                    : (d.thickness || '\u2014')
                                                }
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-sm">
                                                {hasPriceVariants ? (
                                                    <div className="space-y-0.5">
                                                        {d.price_variants.map((v, i) => (
                                                            <div key={i} className="flex items-center justify-end gap-1.5">
                                                                <span className="text-[10px] text-muted-foreground">{v.thickness_text}:</span>
                                                                <span>{v.eur_price != null ? formatCurrency(v.eur_price, 'EUR') : '\u2014'}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    d.eur_price ? formatCurrency(d.eur_price, 'EUR') : '\u2014'
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-sm font-medium">
                                                {d.moq ? d.moq.toLocaleString() : '\u2014'}
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={`${getStatusColor(d.status)} text-xs`}>
                                                    {getStatusLabel(d.status)}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Pagination */}
                {totalCount > PAGE_SIZE && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-border" data-testid="pagination">
                        <p className="text-xs text-muted-foreground">
                            Showing {startItem}–{endItem} of {totalCount}
                        </p>
                        <div className="flex items-center gap-1">
                            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => goToPage(page - 1)} data-testid="prev-page-btn">
                                <CaretLeft size={14} />
                            </Button>
                            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                                let p;
                                if (totalPages <= 7) {
                                    p = i + 1;
                                } else if (page <= 4) {
                                    p = i + 1;
                                } else if (page >= totalPages - 3) {
                                    p = totalPages - 6 + i;
                                } else {
                                    p = page - 3 + i;
                                }
                                return (
                                    <Button
                                        key={p}
                                        variant={p === page ? 'default' : 'outline'}
                                        size="icon"
                                        className="h-8 w-8 text-xs"
                                        onClick={() => goToPage(p)}
                                        data-testid={`page-btn-${p}`}
                                    >
                                        {p}
                                    </Button>
                                );
                            })}
                            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => goToPage(page + 1)} data-testid="next-page-btn">
                                <CaretRight size={14} />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Export Dialog */}
            <Dialog open={exportOpen} onOpenChange={setExportOpen}>
                <DialogContent className="max-w-md" data-testid="export-dialog">
                    <DialogHeader>
                        <DialogTitle>Export Products</DialogTitle>
                        <DialogDescription>
                            {activeFilterCount > 0
                                ? `Exporting ${totalCount} filtered products`
                                : `Exporting all ${totalCount} products`
                            }
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-5">
                        <div>
                            <p className="text-sm font-medium mb-1">Default columns</p>
                            <p className="text-xs text-muted-foreground">SKU, Product Name, Category, Subcategory, Decor, Dimensions, Thickness, EUR/USD Price, MOQ, Status</p>
                        </div>
                        <div>
                            <p className="text-sm font-medium mb-2">Additional data to include</p>
                            <div className="space-y-2.5">
                                {[
                                    { key: 'specs', label: 'Specifications', desc: 'Length, Width, Weight, Material, Surface, Core, Edge' },
                                    { key: 'pricing', label: 'Full Pricing', desc: 'All currencies with units' },
                                    { key: 'packaging', label: 'Packaging', desc: 'Carton dimensions, weight, pallet qty' },
                                    { key: 'media', label: 'Image URLs', desc: 'Primary image and gallery URLs' },
                                ].map(opt => (
                                    <div key={opt.key} className="flex items-start gap-2.5">
                                        <Checkbox
                                            id={`export-${opt.key}`}
                                            checked={exportExtras[opt.key]}
                                            onCheckedChange={() => toggleExtra(opt.key)}
                                            data-testid={`export-check-${opt.key}`}
                                        />
                                        <div className="-mt-0.5">
                                            <Label htmlFor={`export-${opt.key}`} className="text-sm cursor-pointer">{opt.label}</Label>
                                            <p className="text-[11px] text-muted-foreground">{opt.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex gap-2 pt-1">
                            <Button className="flex-1" onClick={() => handleExport('xlsx')} disabled={exporting} data-testid="export-xlsx-btn">
                                <FileXls size={16} className="mr-1.5" />
                                {exporting ? 'Exporting...' : 'Download Excel'}
                            </Button>
                            <Button variant="outline" className="flex-1" onClick={() => handleExport('csv')} disabled={exporting} data-testid="export-csv-btn">
                                <FileCsv size={16} className="mr-1.5" />
                                {exporting ? 'Exporting...' : 'Download CSV'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
